// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import QtyField, { QtyFieldStyles } from "@tps/App/Components/Qty/QtyField";
import { mergeWithCss } from "@tps/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    // XNG-Change
    extendedStyles?: ProductQuantityStyles;
    showActionButton?: boolean;
    showLabel?: boolean;
}

export interface ProductQuantityStyles {
    qtyFieldStyles?: QtyFieldStyles;
}

export const productQuantityStyles: ProductQuantityStyles = {};

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, properties },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory, pricing, inventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    // XNG-Change
    showActionButton = true,
    showLabel = false,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productQuantityStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        debouncedChangeHandler(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const debouncedChangeHandler = useCallback(
        debounce(nextValue => {
            setProductQty(nextValue);
        }, 300),
        [],
    );

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement> | string) => {
        // XNG-CHANGES: accepting string qty values
        const value = typeof event === "string" ? event : event.target.value;
        if (Number(value) < 1) {
            return;
        }
        debouncedChangeHandler(value);
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };
    // XNG-CHANGES: replcaing textfiled by QtyField component
    return (
        <QtyField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="qty"
            min={1}
            max={9999}
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            extendedStyles={styles.qtyFieldStyles}
            data-test-selector="product_qtyOrdered"
            showActionButton={showActionButton}
            showLabel={showLabel}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
